import { Check, Image } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useMemo } from 'react';
import {
  PRO_FEATURES,
  PRO_MODELS,
  STANDARD_FEATURES,
  STANDARD_MODELS,
  SVG_SIZE_M,
  TEAMS_FEATURES,
  ULTRA_FEATURES,
  ULTRA_MODELS,
  ULTRA_TEAMS_PREMIUM_MODELS,
} from 'src/constants';
import { EXTERNAL_MODELS_LINK } from 'src/constants/externalLinks';
import { GTMEvent } from 'src/types';
import { sendGTMEvent } from 'src/utils';

interface CommonPlanCardProps {
  isTeams?: boolean;
  isStandardType?: boolean;
  isProType?: boolean;
  isUltraType?: boolean;
}

/**
 * @deprecated
 */
export const CommonPlanCardContent = ({
  isTeams,
  isStandardType,
  isProType,
  isUltraType,
}: CommonPlanCardProps) => {
  const features = useMemo(() => {
    if (isStandardType) {
      return STANDARD_FEATURES;
    }
    if (isProType) {
      return PRO_FEATURES;
    }
    return ULTRA_FEATURES;
  }, [isStandardType, isProType]);

  const models = useMemo(() => {
    if (isStandardType) {
      return STANDARD_MODELS;
    }
    if (isProType) {
      return PRO_MODELS;
    }
    return ULTRA_MODELS;
  }, [isStandardType, isProType]);

  const premiumModels = ULTRA_TEAMS_PREMIUM_MODELS;

  const handleViewAllModelsClick = () => {
    sendGTMEvent(GTMEvent.PAYMENT_VIEW_ALL_MODELS_CLICK);
  };

  return (
    <>
      <div className="nj-plan-section--current-plan-features-list">
        <h5 className="nj-plan-section--current-plan-features-list-title">
          {isStandardType && 'Includes'}
          {isProType &&
            `Everything in ${isTeams ? 'Business ' : ''}Standard, plus`}
          {isUltraType && !isTeams && `Everything in Pro, plus`}
        </h5>

        {features.map((item) => (
          <div
            className="nj-plan-section--current-plan-feature-row"
            key={item.id}
          >
            <Check size={SVG_SIZE_M} />
            <span>{item.title}</span>
          </div>
        ))}
        {isTeams &&
          TEAMS_FEATURES.map((item) => (
            <div
              className="nj-plan-section--current-plan-feature-row"
              key={item.id}
            >
              <Check size={SVG_SIZE_M} />
              <span>{item.title}</span>
            </div>
          ))}

        <hr className="divider" />
      </div>

      <div className="nj-plan-section--current-plan-models-list">
        {!isTeams && (
          <h5 className="nj-plan-section--current-plan-models-list-title">
            <Check size={SVG_SIZE_M} />
            <span>
              <span className="colored">Unlimited </span>
              <span>
                {isStandardType && 'access to 2 AI models *'}
                {isProType && 'access to 6 AI models *'}
                {isUltraType && 'access to 20+ premium AI models *'}
              </span>
            </span>
          </h5>
        )}
        <div
          className={classNames(
            'nj-plan-section--current-plan-models-list-content',
            {
              'nj-plan-section--current-plan-models-list-content--teams':
                isTeams,
              'nj-plan-section--current-plan-models-list-content--individual':
                !isTeams,
            },
          )}
        >
          <div className="nj-plan-section--current-plan--external-models">
            {isTeams && <p className="nj-feature-bold-item">AI Models</p>}
            {models.map((item) => (
              <div
                className="nj-plan-section--current-plan-models-list-item"
                key={item.id}
              >
                {item.icon}
                <div>{item.text}</div>
                {item.isShowImageIcon && (
                  <div className="nj-plan-section--current-plan-models-list-item-image">
                    <Image size={SVG_SIZE_M} weight="fill" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {isTeams && (
            <div
              className={classNames({
                'nj-plan-section--current-plan--premium-models': isTeams,
              })}
            >
              <div className="nj-plan-section--current-plan--premium-models--header">
                <p className="nj-feature-bold-item">
                  Pay-per-use Premium models <br />
                  <span className="nj-upgrade-form--caption">
                    (coming soon)
                  </span>
                </p>
              </div>
              {premiumModels.map((item) => (
                <div
                  className="nj-plan-section--current-plan-models-list-item"
                  key={item.id}
                >
                  {item.icon}
                  <div>{item.text}</div>
                  {item.isShowImageIcon && (
                    <div className="nj-plan-section--current-plan-models-list-item-image">
                      <Image size={SVG_SIZE_M} weight="fill" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isUltraType && (
        <a
          href={EXTERNAL_MODELS_LINK}
          target="_blank"
          rel="noreferrer"
          className={classNames(
            'nj-plan-section--current-plan-models-list-view-all-link',
            {
              'nj-plan-section--current-plan-models-list-view-all-link--teams':
                isTeams,
              'nj-plan-section--current-plan-models-list-view-all-link--individual':
                !isTeams,
            },
          )}
          onClick={handleViewAllModelsClick}
        >
          View all models
        </a>
      )}
    </>
  );
};
